import { useMemo, useState } from "react";
import { Building2, ArrowUpRight } from "lucide-react";
import { CLASS_DOT, CLASS_TEXT, fmtNumber, slaSemaphoreClass } from "./PerfSharedUI";

/**
 * Per-office comparative table (sortable). Clicking a row calls
 * `onSelect(office)` so the dashboard can pin the filter.
 */
export default function PerOfficeTable({ rows, onSelect }) {
  const [sortBy, setSortBy] = useState("leads_received");
  const [dir, setDir] = useState("desc");

  const sorted = useMemo(() => {
    const arr = [...(rows || [])];
    arr.sort((a, b) => {
      let av; let bv;
      if (["compliance_pct"].includes(sortBy)) {
        av = a.sla[sortBy]; bv = b.sla[sortBy];
      } else if (sortBy.includes("_to_") || sortBy === "lead_to_deal") {
        av = a.conversions[sortBy]; bv = b.conversions[sortBy];
      } else {
        av = a.counts[sortBy]; bv = b.counts[sortBy];
      }
      return (dir === "desc" ? -1 : 1) * ((av ?? 0) - (bv ?? 0));
    });
    return arr;
  }, [rows, sortBy, dir]);

  const setSort = (k) => {
    if (sortBy === k) setDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortBy(k); setDir("desc"); }
  };

  const th = (k, label) => (
    <button
      onClick={() => setSort(k)}
      className={`text-[10px] uppercase tracking-[0.1em] font-bold ${sortBy === k ? "text-foreground" : "text-muted-foreground"} hover:text-foreground`}
    >
      {label} {sortBy === k && (dir === "desc" ? "↓" : "↑")}
    </button>
  );

  if (!rows || rows.length === 0) {
    return (
      <div className="bg-card border border-border rounded-2xl p-10 text-center text-muted-foreground" data-testid="per-office-table">
        Sem dados de escritórios no período.
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8" data-testid="per-office-table">
      <div className="flex items-center gap-3 mb-5">
        <Building2 className="w-5 h-5 text-foreground" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
            Performance por escritório
          </div>
          <h3 className="font-display text-2xl">Comparativo</h3>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-2">{th("office", "Escritório")}</th>
              <th className="text-right py-2 px-2">{th("leads_received", "Leads")}</th>
              <th className="text-right py-2 px-2">{th("compliance_pct", "SLA %")}</th>
              <th className="text-right py-2 px-2">{th("estimates_sent", "Estimativas")}</th>
              <th className="text-right py-2 px-2">{th("rt_scheduled", "RT")}</th>
              <th className="text-right py-2 px-2">{th("deals_closed", "Negócios")}</th>
              <th className="text-right py-2 px-2">{th("estimate_to_rt", "Est→RT")}</th>
              <th className="text-right py-2 px-2">{th("lead_to_deal", "Lead→Neg.")}</th>
              <th className="w-4"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const slaCls = slaSemaphoreClass(row.sla.compliance_pct);
              return (
                <tr
                  key={row.office}
                  onClick={() => onSelect && onSelect(row.office)}
                  className="border-b border-border/60 hover:bg-muted/40 cursor-pointer"
                  data-testid={`office-row-${row.office}`}
                >
                  <td className="py-3 px-2 font-semibold text-foreground">{row.office}</td>
                  <td className="py-3 px-2 text-right tabular-nums">{fmtNumber(row.counts.leads_received)}</td>
                  <td className="py-3 px-2 text-right">
                    <span className={`inline-flex items-center gap-1 ${CLASS_TEXT[slaCls]}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${CLASS_DOT[slaCls]}`} />
                      {row.sla.compliance_pct?.toFixed(0)}%
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right tabular-nums">{fmtNumber(row.counts.estimates_sent)}</td>
                  <td className="py-3 px-2 text-right tabular-nums">{fmtNumber(row.counts.rt_scheduled)}</td>
                  <td className="py-3 px-2 text-right tabular-nums font-semibold">{fmtNumber(row.counts.deals_closed)}</td>
                  <td className="py-3 px-2 text-right tabular-nums">{row.conversions.estimate_to_rt?.toFixed(1)}%</td>
                  <td className="py-3 px-2 text-right tabular-nums font-semibold">{row.conversions.lead_to_deal?.toFixed(1)}%</td>
                  <td className="py-3 px-2"><ArrowUpRight className="w-3 h-3 text-muted-foreground/70" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
